'use client';

import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../stores/authStore';
import { Platform } from '../platform';
import { createLocalAuth } from '../utils/localAuth';

// Create platform-specific local auth handler
const localAuth = createLocalAuth();

// Platform-specific cleanup behavior
const shouldCleanupListener = Platform.select({
  web: process.env.NODE_ENV === 'production',
  default: true // Always cleanup on mobile
});

export interface AuthContextType {
  session: Session | null;
  user: User | null;
  signOut: () => Promise<void>;
  isLoading: boolean;
  isInitialized: boolean;
  isAuthenticated: boolean;
}

interface AuthProviderProps {
  children: React.ReactNode;
  onSignOut?: () => void;
  onSignIn?: (event?: string) => void;
  onSignOutRedirect?: () => void;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children, onSignOut, onSignIn, onSignOutRedirect }: AuthProviderProps) {
  const { setUser, setLoading, setInitialized } = useAuthStore();
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUserState] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const initialized = useRef(false);
  const authStateSubscription = useRef<{ subscription?: { unsubscribe: () => void } }>({});

  const updateAuthState = useCallback(async (newSession: Session | null, newUser: User | null) => {
    if (!initialized.current) {
      const canProceed = await localAuth.handleLocalAuth();
      if (!canProceed) {
        console.debug('[Auth] Local auth failed, signing out');
        await supabase.auth.signOut();
        return;
      }

      console.debug('[Auth] Initializing auth state');
      setSession(newSession);
      setUserState(newUser);
      setUser(newUser);
      setLoading(false);
      setInitialized(true);
      setIsLoading(false);
      initialized.current = true;

      // Save session if exists
      if (newSession) {
        await localAuth.saveSession(JSON.stringify(newSession));
      } else {
        await localAuth.removeSession();
      }
      return;
    }

    // Only update if there's an actual change
    const hasSessionChanged = (newSession?.access_token !== session?.access_token);
    const hasUserChanged = (newUser?.id !== user?.id);

    if (hasSessionChanged || hasUserChanged) {
      console.debug('[Auth] Auth state changed', { hasSessionChanged, hasUserChanged });
      setSession(newSession);
      setUserState(newUser);
      setUser(newUser);

      // Update stored session
      if (newSession) {
        await localAuth.saveSession(JSON.stringify(newSession));
      } else {
        await localAuth.removeSession();
      }
    }
  }, [session, user, setUser, setLoading, setInitialized]);

  useEffect(() => {
    let isMounted = true;
    // Store ref value at the start of the effect
    const currentSubscription = authStateSubscription.current;

    const initializeAuth = async () => {
      try {
        console.debug('[Auth] Starting auth initialization');
        const { data: { session: initialSession } } = await supabase.auth.getSession();
        
        if (isMounted) {
          if (initialSession) {
            console.debug('[Auth] Initial session loaded');
            await updateAuthState(initialSession, initialSession.user);
            onSignIn?.('SESSION_RESTORED');
          } else {
            console.debug('[Auth] No initial session');
            await updateAuthState(null, null);
          }
        }
      } catch (error) {
        console.error('[Auth] Failed to get initial session:', error);
        if (isMounted) {
          await updateAuthState(null, null);
        }
      }
    };

    const setupAuthListener = () => {
      if (currentSubscription.subscription) {
        console.debug('[Auth] Auth listener already setup');
        return;
      }

      console.debug('[Auth] Setting up auth listener');
      const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
        if (!isMounted) return;

        console.debug('[Auth] Auth state changed:', event);
        
        await updateAuthState(newSession, newSession?.user ?? null);

        if (event === 'SIGNED_IN') {
          console.debug('[Auth] User signed in');
          onSignIn?.(event);
        } else if (event === 'SIGNED_OUT') {
          console.debug('[Auth] User signed out');
          await localAuth.removeSession();
          onSignOut?.();
        }
      });

      authStateSubscription.current.subscription = subscription;
    };

    initializeAuth();
    setupAuthListener();

    return () => {
      isMounted = false;
      // Use the stored ref value in cleanup
      const subscription = currentSubscription.subscription;
      if (subscription && shouldCleanupListener) {
        console.debug('[Auth] Cleaning up auth listener');
        subscription.unsubscribe();
      }
    };
  }, [updateAuthState, onSignOut, onSignIn]);

  const signOut = async () => {
    try {
      setIsLoading(true);
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      setUser(null);
      setSession(null);
      onSignOutRedirect?.();
    } catch (error) {
      console.error('Sign out error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      session, 
      user, 
      signOut, 
      isLoading, 
      isInitialized: initialized.current,
      isAuthenticated: !!user && !!session
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 